import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

import { formatDate, formatDateTime } from "@/lib/datetime";
/** L1 Fabrication Issue Slip — 1/4 A4 (A6, 105mm × 148mm portrait) worker hand-out. */
export default function L1IssueSlipPrint() {
  const { id } = useParams();
  const [batch, setBatch] = useState(null);
  const [biz, setBiz] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [b, s] = await Promise.all([
          api.get(`/production/fabrications/${id}`),
          api.get("/sales/business-settings").catch(() => ({ data: {} })),
        ]);
        setBatch(b.data);
        setBiz(s.data || {});
      } finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => { if (!loading && batch) setTimeout(() => window.print(), 400); }, [loading, batch]);

  if (loading) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!batch) return <div className="p-8 text-red-600">Batch not found</div>;

  const totalQty = (batch.rm_lines || []).reduce((s, l) => s + Number(l.issued_qty || 0), 0);
  const issuedAt = formatDateTime(batch.created_at);

  return (
    <div className="min-h-screen bg-gray-200 print:bg-white">
      <style>{`
        @page { size: 105mm 148mm; margin: 4mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          .slip { box-shadow: none !important; margin: 0 !important; }
        }
        .slip { width: 105mm; min-height: 148mm; font-family: 'Inter', system-ui, sans-serif; }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 p-3 flex items-center gap-3 shadow-sm">
        <button onClick={() => window.history.back()} className="h-9 w-9 rounded-md border border-gray-200 hover:bg-gray-50 flex items-center justify-center"><ArrowLeft className="h-4 w-4" /></button>
        <span className="font-heading font-semibold text-gray-900">Issue Slip — {batch.batch_no}</span>
        <button onClick={() => window.print()} className="ml-auto h-9 px-3 rounded-md bg-[#9B1C1C] hover:bg-[#7F1D1D] text-white text-sm font-semibold flex items-center gap-1.5" data-testid="l1-issue-print-do">
          <Printer className="h-4 w-4" /> Print
        </button>
      </div>

      <div className="max-w-max mx-auto my-6 print:my-0 slip bg-white shadow-sm print:shadow-none p-4 text-gray-900 text-[10px] leading-tight">
        {/* Header */}
        <div className="border-b-2 border-[#9B1C1C] pb-2 mb-2">
          <div className="font-heading font-bold text-[14px] text-[#9B1C1C] leading-tight">{biz.business_name || "Thara Gold"}</div>
          {biz.gstin && <div className="text-[8px] font-mono text-gray-600">GSTIN: {biz.gstin}</div>}
          <div className="flex items-center justify-between mt-1">
            <div className="uppercase tracking-widest text-[9px] font-semibold text-gray-700">L1 Fabrication Issue Slip</div>
            <div className="font-mono font-bold text-[11px] text-[#9B1C1C]">{batch.batch_no}</div>
          </div>
        </div>

        {/* Batch meta */}
        <div className="grid grid-cols-2 gap-1 mb-2 text-[9px]">
          <div><span className="text-gray-500">Prod. Date:</span> <span className="font-semibold">{formatDate(batch.production_date)}</span></div>
          <div><span className="text-gray-500">Issued At:</span> <span className="font-mono">{issuedAt}</span></div>
          <div className="col-span-2"><span className="text-gray-500">Worker:</span> <span className="font-semibold">{batch.worker_name}</span>{batch.worker_type && <span className="text-gray-500"> · {batch.worker_type}</span>}</div>
          {batch.notes && <div className="col-span-2"><span className="text-gray-500">Notes:</span> {batch.notes}</div>}
        </div>

        {/* RM lines */}
        <table className="w-full text-[9px] border border-gray-300 mb-2">
          <thead className="bg-gray-100">
            <tr className="text-left border-b border-gray-300">
              <th className="px-1.5 py-1 w-4 text-center">#</th>
              <th className="px-1.5 py-1">Raw Material</th>
              <th className="px-1.5 py-1 w-8 text-center">Unit</th>
              <th className="px-1.5 py-1 w-12 text-right">Qty</th>
            </tr>
          </thead>
          <tbody>
            {(batch.rm_lines || []).map((l, i) => (
              <tr key={i} className="border-b border-gray-200">
                <td className="px-1.5 py-1 text-center text-gray-500">{i + 1}</td>
                <td className="px-1.5 py-1">
                  <div className="font-semibold leading-tight">{l.raw_material_name}</div>
                </td>
                <td className="px-1.5 py-1 text-center text-gray-600">{l.unit || "—"}</td>
                <td className="px-1.5 py-1 text-right tabular-nums font-semibold">{l.issued_qty}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 border-t-2 border-[#9B1C1C]">
              <td colSpan={3} className="px-1.5 py-1 text-right font-semibold">Total</td>
              <td className="px-1.5 py-1 text-right tabular-nums font-heading font-bold text-[#9B1C1C]">{totalQty}</td>
            </tr>
          </tbody>
        </table>

        {/* Instructions */}
        <div className="text-[8px] text-gray-600 mb-2 leading-snug italic border border-dashed border-gray-300 p-1.5 rounded">
          Please return all fabricated pieces along with any wastage. Worker charges are computed on the received qty only.
        </div>

        {/* Signatures */}
        <div className="grid grid-cols-2 gap-4 mt-4 text-[9px]">
          <div>
            <div className="border-t border-gray-500 pt-0.5 text-center font-semibold">Issued By</div>
          </div>
          <div>
            <div className="border-t border-gray-500 pt-0.5 text-center font-semibold">Worker Signature</div>
          </div>
        </div>
      </div>
    </div>
  );
}
